/**
 * A foto da tela do visitante, tirada na PÁGINA DO CLIENTE.
 *
 *   captureScreen()
 *      │
 *      ├─ 1. applyMask()      style temporário: senha, cartão e data-gd-mask saem borrados
 *      │        │             (política de privacidade — mora aqui, não na lib de captura)
 *      │        ▼
 *      ├─ 2. GetDemoBrowser.captureScreenshot({ area:'viewport', exclude: WIDGET_SELECTORS })
 *      │        │             a lib rasteriza e devolve a data URL
 *      │        ▼
 *      ├─ 3. restore()        SEMPRE, no finally — ver nota abaixo
 *      │
 *      └─ 4. dataUrl → Blob   o que atravessa o postMessage (structured clone, sem base64)
 *
 * A nota do passo 3 é a razão de este arquivo existir separado: se a rasterização estourar entre
 * aplicar e remover o borrado, a página DO CLIENTE fica borrada permanentemente. Não é um bug
 * nosso: é o sistema dele quebrado na frente do usuário. Por isso há `finally` E um timeout de
 * guarda independente.
 */

export const WIDGET_SELECTORS = [
  '#getdemo-bot-player',
  '#getdemo-backdrop',
  '#getdemo-slide-overlay',
  '#getdemo-slide-close',
  '#getdemo-slide-content',
  '#getdemo-table-overlay',
  '#getdemo-drag-targets',
  '[data-getdemo-interaction-highlight]',
  '[data-getdemo-coordinate-highlight]'
];

/**
 * Masking conservador: o que é inequivocamente sensível, mais o que o cliente marcar. Cobrir o
 * texto inteiro da página (como o Sentry faz por padrão) tornaria o print ilegível para o modelo,
 * que é o motivo de tirá-lo.
 */
export const MASK_SELECTORS = [
  'input[type="password"]',
  '[autocomplete*="cc-"]',
  '[name*="card"]',
  '[name*="cartao"]',
  '[data-gd-mask]',
  '[data-gd-block]'
];

const MASK_STYLE_ID = 'getdemo-capture-mask-style';
const MASK_CLASS = 'gd-capture-masked';
const RESTORE_GUARD_MS = 10000;

export const CAPTURE_MAX_WIDTH = 1536;
export const CAPTURE_FORMAT = 'png' as const;
export const CAPTURE_MIME = 'image/png' as const;

export type ScreenCaptureErrorCode = 'unsupported' | 'tainted' | 'capture_failed' | 'empty';

export class ScreenCaptureError extends Error {
  constructor(readonly code: ScreenCaptureErrorCode, message?: string) {
    super(message || code);
    this.name = 'ScreenCaptureError';
  }
}

export interface ScreenCaptureOutput {
  blob: Blob;
  width: number;
  height: number;
  maskedCount: number;
}

export function applyMask(doc: Document = document): {
  maskedCount: number;
  restore: () => void;
} {
  let maskedCount = 0;
  const masked: Element[] = [];

  for (const selector of MASK_SELECTORS) {
    let found: NodeListOf<Element>;
    try {
      found = doc.querySelectorAll(selector);
    } catch {
      continue;
    }
    found.forEach(el => {
      if (masked.includes(el)) return;
      el.classList.add(MASK_CLASS);
      masked.push(el);
      maskedCount++;
    });
  }

  // Retângulo SÓLIDO, não blur. Cor de fundo e texto transparente são propriedades que a
  // rasterização sempre respeita e que o e2e consegue VERIFICAR nos pixels da foto — um filtro
  // dependeria do suporte do rasterizador, e proteção que não dá para provar não é proteção. É o
  // mesmo motivo pelo qual o Sentry pinta retângulo em vez de desfocar.
  const style = doc.createElement('style');
  style.id = MASK_STYLE_ID;
  style.textContent =
    `.${MASK_CLASS}{color:transparent !important;` +
    `background-color:#94a3b8 !important;background-image:none !important;` +
    `border-color:#94a3b8 !important;text-shadow:none !important;` +
    `-webkit-text-fill-color:transparent !important;}`;
  doc.head.appendChild(style);

  let restored = false;
  const restore = () => {
    if (restored) return;
    restored = true;
    masked.forEach(el => el.classList.remove(MASK_CLASS));
    style.remove();
  };

  setTimeout(restore, RESTORE_GUARD_MS);
  return { maskedCount, restore };
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, payload] = dataUrl.split(',');
  if (!payload) throw new ScreenCaptureError('empty', 'data url sem conteúdo');
  const mime = header.match(/data:([^;]+)/)?.[1] || CAPTURE_MIME;
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function isTaintError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  const message = String((err as { message?: string } | null)?.message ?? '');
  return name === 'SecurityError' || /tainted|cross-origin/i.test(message);
}

export async function captureScreen(): Promise<ScreenCaptureOutput> {
  const api = window.GetDemoBrowser;
  if (!api || typeof api.captureScreenshot !== 'function') {
    throw new ScreenCaptureError('unsupported');
  }

  const { maskedCount, restore } = applyMask();

  try {
    const shot = await api.captureScreenshot({
      area: 'viewport',
      exclude: WIDGET_SELECTORS,
      maxWidth: CAPTURE_MAX_WIDTH,
      format: CAPTURE_FORMAT
    });

    if (!shot?.dataUrl) throw new ScreenCaptureError('empty');

    return {
      blob: dataUrlToBlob(shot.dataUrl),
      width: shot.width,
      height: shot.height,
      maskedCount
    };
  } catch (err) {
    if (err instanceof ScreenCaptureError) throw err;
    // Navegador recusando gerar a imagem por conteúdo de outro domínio na página. Não é erro
    // nosso e não tem conserto no código: o player orienta o print manual, que já funciona.
    if (isTaintError(err)) throw new ScreenCaptureError('tainted');
    throw new ScreenCaptureError(
      'capture_failed',
      err instanceof Error ? err.message : String(err)
    );
  } finally {
    restore();
  }
}
